import { ref } from "vue";
import axios from "axios";

export function useGoogleAuth() {
  const isGoogleLoading = ref(false);
  const BASE_URL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";
  const GOOGLE_CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;

  let googleInitialized = false;
  let onSuccessCallback = null;
  let onErrorCallback = null;
  let onProcessingStartCallback = null;

  /**
   * Initialize Google Identity Services
   */
  const initializeGoogleAuth = ({
    onSuccess,
    onError,
    onProcessingStart,
  } = {}) => {
    return new Promise((resolve, reject) => {
      if (googleInitialized) {
        resolve();
        return;
      }

      onSuccessCallback = onSuccess;
      onErrorCallback = onError;
      onProcessingStartCallback = onProcessingStart;

      // Load Google Identity Services script
      if (
        !document.querySelector(
          'script[src="https://accounts.google.com/gsi/client"]'
        )
      ) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          initializeGoogleServices();
          resolve();
        };
        script.onerror = () => {
          reject(new Error("Failed to load Google Identity Services"));
        };
        document.head.appendChild(script);
      } else {
        if (window.google?.accounts?.id) {
          initializeGoogleServices();
          resolve();
        } else {
          const checkGoogle = setInterval(() => {
            if (window.google?.accounts?.id) {
              clearInterval(checkGoogle);
              initializeGoogleServices();
              resolve();
            }
          }, 100);
          setTimeout(() => {
            clearInterval(checkGoogle);
            reject(
              new Error("Google Identity Services initialization timeout")
            );
          }, 10000);
        }
      }
    });
  };

  /**
   * Initialize Google Services after script loads
   */
  const initializeGoogleServices = () => {
    if (!window.google?.accounts?.id || !GOOGLE_CLIENT_ID) {
      console.error(
        "Google Identity Services not available or client ID missing"
      );
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: "signin",
        ux_mode: "popup",
        use_fedcm_for_prompt: true,
      });

      window.google.accounts.id.renderButton(
        document.getElementById("g_id_signin"),
        {
          theme: "outline",
          size: "large",
          width: "100%",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
        }
      );

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log(
            "One Tap prompt not displayed:",
            notification.getNotDisplayedReason()
          );
        }
      });

      googleInitialized = true;
      console.log("✅ Google Identity Services initialized successfully");
    } catch (error) {
      console.error("❌ Error initializing Google Identity Services:", error);
      if (onErrorCallback) {
        onErrorCallback(error);
      }
    }
  };

  /**
   * Extract email from Google JWT credential
   */
  const extractEmailFromCredential = (credential) => {
    try {
      const payload = JSON.parse(atob(credential.split(".")[1]));
      return payload.email;
    } catch (error) {
      console.error("Failed to decode Google credential:", error);
      return null;
    }
  };

  /**
   * Handle credential response from Google
   */
  const handleCredentialResponse = async (response) => {
    if (!response.credential) {
      const error = new Error("No credential received from Google");
      console.error(error);
      if (onErrorCallback) {
        onErrorCallback(error);
      }
      return;
    }

    // Extract email from credential for error handling
    const userEmail = extractEmailFromCredential(response.credential);

    try {
      isGoogleLoading.value = true;
      // Start processing loading
      if (onProcessingStartCallback) {
        onProcessingStartCallback();
      }

      const backendResult = await sendCredentialToBackend(response.credential);

      // ✅ Only use the result for display
      if (onSuccessCallback) {
        onSuccessCallback({
          source: "google-auth",
          ...backendResult,
        });
      }
    } catch (error) {
      console.error("❌ Error processing Google credential:", error);
      if (onErrorCallback) {
        // Pass both error and email to the error callback
        onErrorCallback(error, userEmail);
      }
    } finally {
      isGoogleLoading.value = false;
    }
  };

  /**
   * Send Google credential to backend
   */
  const sendCredentialToBackend = async (credential) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/auth/google-login`,
        {
          credential: credential,
          clientId: GOOGLE_CLIENT_ID,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 10000,
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        // Create a proper error object with response details for 404 detection
        const customError = new Error(
          error.response.data?.error || "Authentication failed"
        );
        customError.response = {
          status: error.response.status,
          data: error.response.data,
        };
        throw customError;
      } else if (error.request) {
        throw new Error("Network error. Please check your connection.");
      } else {
        throw new Error("Authentication failed. Please try again.");
      }
    }
  };

  /**
   * Handle manual Google login
   */
  const handleGoogleLogin = async (credentialResponse) => {
    if (credentialResponse?.credential) {
      return await sendCredentialToBackend(credentialResponse.credential);
    } else {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.prompt();
      } else {
        throw new Error("Google Sign-In not initialized");
      }
    }
  };

  /**
   * Cleanup
   */
  const cleanup = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.cancel();
    }
    googleInitialized = false;
    onSuccessCallback = null;
    onErrorCallback = null;
    onProcessingStartCallback = null;
  };

  /**
   * Revoke session
   */
  const revokeGoogleAuth = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  return {
    isGoogleLoading,
    initializeGoogleAuth,
    handleGoogleLogin,
    cleanup,
    revokeGoogleAuth,
  };
}
